import React, { useCallback, useContext, useEffect } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify/unstyled";
import { ReportContext } from "@sscs/report/report";
import { ToastNotificationTypes } from "@sscs/types";
import { Toast } from "@sscs/ui";
import { ReanalyzeDialog } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useDialog } from "Hooks";
import {
  useGetReportSummaryQuery,
  useGetSingleFileQuery,
  useGetVersionQuery,
  useReprocessFileMutation,
  useReprocessVersionMutation,
} from "Services";
import { apiErrorToast } from "Utils";

// Outdated report notification text
export const REANALYZED_REPORT =
  "This file has been reanalyzed. Refresh the page to see the newest report.";

// Stable references so they can be used safely as effect dependencies.
const FILESTREAM_REANALYZE_PERMISSIONS = [PERMISSION.REPROCESS_FILE];
const VERSION_REANALYZE_PERMISSIONS = [PERMISSION.REANALYZE_VERSION];

// Title shared by the "outdated" and "reanalyzing" states.
const OUTDATED_TITLE = "This report is outdated.";

type NotificationHandlerProps = {
  fileId: string;
  isFileStream: boolean;
  isReportDeleted: boolean;
  children: React.JSX.Element;
};

const NotificationHandler = ({
  fileId,
  isFileStream,
  isReportDeleted,
  children,
}: NotificationHandlerProps): React.JSX.Element => {
  const { reportId, diffId } = useContext(ReportContext);
  const { data: reportSummaryData } = useGetReportSummaryQuery({
    diff_report_id: diffId,
    ui_id: reportId,
  });
  const [reanalyzeVersionMutation] = useReprocessVersionMutation();
  const [reanalyzeFileMutation] = useReprocessFileMutation();

  // without destructuring eslint wants the whole object in useEffect dep array,
  // which will trigger useEffect every render.
  const {
    isOpen: dialogIsOpen,
    open: dialogOpen,
    close: dialogClose,
  } = useDialog();
  const toastId = `notification-handler-${reportId}`;

  const reanalyzePermissions = isFileStream
    ? FILESTREAM_REANALYZE_PERMISSIONS
    : VERSION_REANALYZE_PERMISSIONS;

  const canReanalyze = useAllowedTo({
    permissions: reanalyzePermissions,
  });

  const { data: singleFile } = useGetSingleFileQuery(
    isFileStream ? fileId : skipToken,
  );

  const { data: version } = useGetVersionQuery(
    !isFileStream ? { versionId: fileId } : skipToken,
  );

  const fileName =
    (isFileStream ? singleFile?.file_name : version?.software_info.product) ??
    "";

  useEffect(() => {
    return () => {
      toast.dismiss(toastId);
    };
  }, [toastId]);

  const onRefreshClick = useCallback(() => {
    const currentUrl = new URL(window.location.href);

    window.location.href = currentUrl.toString();
  }, []);

  useEffect(() => {
    // if the reanalyze dialog is open we don't want to trigger any toast
    // updates, and when it subsequently closes the toast will reappear
    // (if it should due to the conditionals)
    if (dialogIsOpen) return;

    const notificationExists = toast.isActive(toastId);
    if (isReportDeleted) toast.dismiss(toastId);
    if (!reportSummaryData) return;

    const isRepro =
      reportSummaryData.report_id ===
      version?.artifacts_summary.repro?.latest_report_summary?.report_id;
    const isReanalyzing = isFileStream
      ? singleFile?.current_status.code === "PROCESSING"
      : (isRepro
          ? version?.artifacts_summary.repro?.current_status.code
          : version?.artifacts_summary.version.current_status.code) ===
        "PROCESSING";

    if (!isReanalyzing && !reportSummaryData.is_outdated) return;

    let data: ToastNotificationTypes;

    if (isReanalyzing) {
      data = {
        title: OUTDATED_TITLE,
        description: `Reason: ${reportSummaryData.outdated_reason ?? "Reanalyze"}`,
        // Permission-gated: only reanalyzers see the (disabled) in-progress action.
        actions: canReanalyze
          ? [{ label: "Reanalyzing file...", disabled: true }]
          : undefined,
        type: "warning",
      };
    } else if (reportSummaryData.is_latest) {
      // report is outdated
      data = {
        title: OUTDATED_TITLE,
        description: `Reason: ${reportSummaryData.outdated_reason ?? ""}`,
        actions: canReanalyze
          ? [{ label: "Reanalyze", onClick: dialogOpen }]
          : undefined,
        type: "warning",
      };
    } else {
      data = {
        title: REANALYZED_REPORT,
        actions: [{ label: "Refresh", onClick: onRefreshClick }],
        type: "warning",
      };
    }

    data.props = {
      toastId: toastId,
      autoClose: false,
      closeButton: true,
      closeOnClick: !canReanalyze, // users who aren't allowed to reanalyze files will need to close click the notification. other users will have the button
    };

    if (notificationExists) {
      Toast.update(toastId, data);
    } else {
      Toast.show(data);
    }
  }, [
    toastId,
    version?.artifacts_summary,
    canReanalyze,
    isReportDeleted,
    isFileStream,
    reanalyzePermissions,
    onRefreshClick,
    reportSummaryData,
    singleFile?.current_status.code,
    dialogIsOpen,
    dialogOpen,
  ]);

  return (
    <>
      {children}
      <ReanalyzeDialog
        fileName={fileName}
        isOpen={dialogIsOpen}
        onClose={dialogClose}
        onConfirm={async (notifyOnCompletion) => {
          if (isFileStream) {
            try {
              await reanalyzeFileMutation({
                fileId: fileId,
                notify_on_completion: notifyOnCompletion,
              }).unwrap();
            } catch (error) {
              apiErrorToast(error, "Could not reanalyze file");
            }
          } else {
            try {
              await reanalyzeVersionMutation({
                versionId: fileId,
                notify_on_completion: notifyOnCompletion,
              }).unwrap();
            } catch (error) {
              apiErrorToast(error, "Could not reanalyze version");
            }
          }
        }}
      />
    </>
  );
};

export default NotificationHandler;
